import React, { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import classNames from "classnames";
import { omit } from "underscore";
import { batch } from "react-redux";

import {
	SessionPanelLayoutsTypes,
	LanguagesAbbr,
	MuxStreamData,
	MuxStreamStatus
} from "../../../../../../../types/working-model";
import { useTypedSelector } from "../../../../../../../store/reducers/use-typed-selector";
import { LanguageOption } from '../../../editor/agenda-editor/select-languages';
import PanelBody from "../panel-body";
import { SessionPanelMap } from "@admin/create/session/navigation/panel/session-panel-route-map";
import { useGetAdminUrl } from "utils/admin-routing-utils";
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import { OptionalComponent } from "utils/optional-component";
import languageLabelMap from '../../../../../../../utils/translate-langs-map.json';
import languageLabelMapV2 from '../../../../../../../utils/translate-langs-map-v2.json';
import GradientText from "@general-ui/gradient-text/gradient-text";
import useClickAwayListener from "utils/use-click-away-listener";
import { Tooltip } from "@general-ui/tooltip/tooltip";
import Switch from "@general-ui/switch/switch";
import NavigationDropdown from "@general-ui/dropdown/navigation-dropdown";
import { UpdateSelectedEventDefaultLanguage } from 'connection/events';
import { loadWorkingSession, updateWorkingSession } from 'store/actions/admin/create-event/session';
import { hideAlert, showAlert, showAlertLong } from "@general-ui/alert/alert-service";
import { sessionLanguagesUpdated, setWorkingEvent, updateSessionInfo } from "store/actions/admin/create-event";
import AddLanguageConfirmationModal from "./modals/add-language-to-sessions-modal";
import RemoveLanguageConfirmationModal from "./modals/remove-language-modal";
import { GetMuxStreamData } from "connection/mux";
import { FetchError } from "connection/helpers";

import "./language-and-captions.scss";

const LanguageAndCaptions: React.FC = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const adminPath = useGetAdminUrl();

	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const [savingEventBaseLanguage, setSavingEventBaseLanguage] = useState(false);
	const [languageDropdownsOpen, setLanguageDropdownsOpen] = useState<{[key: string]: boolean}>({});
	const [languageToAdd, setLanguageToAdd] = useState<LanguagesAbbr | null>(null);
	const [languageToRemove, setLanguageToRemove] = useState<LanguagesAbbr | null>(null);
	const [addLanguageDropdownOpen, setAddLanguageDropdownOpen] = useState(false);
	const [loadingLiveStreamData, setLoadingLiveStreamData] = useState(false);
	const [liveStreamDataLoaded, setLiveStreamDataLoaded] = useState(false);
	const [liveStreamData, setLiveStreamData] = useState<MuxStreamData | null>(null);

	const addLanguageButtonRef = useRef<HTMLDivElement>(null);
	useClickAwayListener({
		ref: addLanguageButtonRef,
		onClickAway: () => null,
		onMouseupClickAway: () => setAddLanguageDropdownOpen(false),
		onEscape: () => setAddLanguageDropdownOpen(false),
		open: addLanguageDropdownOpen
	});

	const baseLanguage = useMemo(() => workingEvent?.settings.i18n?.base, [workingEvent]);
	const eventLanguages = useMemo(() => workingEvent?.settings.i18n?.event_languages, [workingEvent]);

	useEffect(() => {
		if (liveStreamDataLoaded || loadingLiveStreamData || !workingSession?.uuid || !baseLanguage) return;

		(async () => {
			try {
				setLoadingLiveStreamData(true);
				const muxStreamData = await GetMuxStreamData(workingSession?.uuid, baseLanguage);
				setLiveStreamData(muxStreamData);
			} catch (error: unknown) {
				if (error instanceof FetchError && error.status !== 404) {
					console.error('Could not get Mux stream data: ', error);
					showAlert({
						message: 'Error checking live stream status',
						description: 'Please try again, or contact support if the issue persists.',
						type: 'error',
						duration: 10000
					});
				}
			}

			setLiveStreamDataLoaded(true);
			setLoadingLiveStreamData(false);
		})();
	}, [liveStreamDataLoaded, loadingLiveStreamData, workingSession?.uuid, baseLanguage]);

	const canEditExtendedDictionary = !!(
		// No stream created yet
		(!loadingLiveStreamData && !liveStreamData) || 
		// Stream exists but is not live
		!!(!loadingLiveStreamData && liveStreamData?.state && liveStreamData.state !== MuxStreamStatus.active)
	);

	const languageOptions: LanguageOption[] = useMemo(() => {
		if (!eventLanguages) return [];
		// Base first, then the rest alphabetized
		return eventLanguages.map(eventLang => ({ code: eventLang, label: languageLabelMap[eventLang] }))
			.sort((a, b) => a.label > b.label ? 1 : -1)
			.sort((a, _) => a.code === baseLanguage ? -1 : 1);
	}, [eventLanguages, baseLanguage]);

	const languageOptionsSet: Set<string> = useMemo(() => {
		return new Set(languageOptions.map(languageOption => languageOption.code));
	}, [languageOptions]);

	const navigateToUpload = () => history.push(
		adminPath({
			path: SessionPanelMap[SessionPanelLayoutsTypes.LanguageAndCaptionsUpload]
		})
	);

	const navigateToExtendedDictionary = () => history.push(
		adminPath({
			path: SessionPanelMap[SessionPanelLayoutsTypes.ExtendedDictionary]
		})
	);

	const handleLanguageDropdownOpen = (language: string) => {
		if (languageDropdownsOpen[language]) {
			setLanguageDropdownsOpen(omit(languageDropdownsOpen, language));
		} else {
			setLanguageDropdownsOpen({ ...languageDropdownsOpen, [language]: true });
		}
	};

	const handleBaseLanguageChange = useCallback(async (newDefaultLang: string) => {
		let changingLanguageAlert;
		try {
			if (token && newDefaultLang && workingEvent && newDefaultLang !== workingEvent?.settings?.i18n?.base) {
				setSavingEventBaseLanguage(true);

				changingLanguageAlert = showAlertLong({
					message: 'Updating event language',
					description: 'Please wait while we update your project content. This may take a few moments.',
					spinner: true
				}) as string;

				const event = await UpdateSelectedEventDefaultLanguage({ token, eventUuid: workingEvent.uuid, language: newDefaultLang });
				dispatch(setWorkingEvent(event));

				if (workingSession) {
					const replacementSession = event.sessions.find(session => session.uuid === workingSession.uuid);
					if (replacementSession) {
						dispatch(loadWorkingSession(replacementSession));
					}
				}
			}
		} catch (e) {
			console.error(e);
			showAlert({
				message: 'Error updating default language',
				description: 'We were unable to change your project default language. Please ensure that your project has content in the selected language.',
				type: 'error'
			});
		} finally {
			if (changingLanguageAlert) {
				hideAlert({ id: changingLanguageAlert });
			}

			setSavingEventBaseLanguage(false);
		}
	}, [dispatch, token, workingEvent, workingSession]);

	const handleLanguageEnablementToggle = useCallback((languageToChange: LanguagesAbbr) => {
		try {
			if (workingEvent && workingSession && languageToChange && token) {
				const updatedWorkingSession = {
					...workingSession,
					languages: workingSession.languages.includes(languageToChange) 
						? workingSession.languages.filter(lang => lang !== languageToChange)
						: [...workingSession.languages, languageToChange]
				};

				batch(() => {
					dispatch(updateSessionInfo(updatedWorkingSession, token));
					dispatch(updateWorkingSession(updatedWorkingSession));
					dispatch(sessionLanguagesUpdated(workingSession.session, updatedWorkingSession.languages as LanguagesAbbr[]));
				});
			}
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to add language',
				description: 'Please try again, or contact support if this persists',
				type: 'error'
			});
		}
	}, [dispatch, workingEvent, workingSession, token]);

	return (
		<PanelBody className="language-and-captions">
			{languageOptions.map((lang, idx) => {
				const isOpen = !!languageDropdownsOpen[lang.code];
				const languageIsBase = !!(baseLanguage && baseLanguage === lang.code);

				return (
					<div className={classNames('language-option', { open: isOpen })} style={idx === 0 ? { marginTop: 16 } : {}} key={lang.code}>
						<div className="header">
							<div className="left">
								<span className="language-label">{lang.label}</span>
								<span className="language-code">{lang.code}</span>
								<OptionalComponent display={languageIsBase}>
									<span className="language-is-base">Base</span>
								</OptionalComponent>
							</div>
							<div className="right">
								<OptionalComponent display={!languageIsBase}>
									<NavigationDropdown 
										title={<Icon name={ICONS.THREE_DOTS_VERTICAL} color={COLORS.WHITE} size={16} />} 
										buttonClassName="clear" 
										isArrow={false}
										edge="right"
									>
										<OptionalComponent display={!languageIsBase}>
											<button className="clear sharp btn-left-align" disabled={savingEventBaseLanguage} onClick={() => handleBaseLanguageChange(lang.code)}>
												<span>Make base language</span>
											</button>
										</OptionalComponent>
										<button className="clear sharp btn-left-align" onClick={() => alert('Not Yet Implemented TODO')}>
											<span>Add to all sessions</span>
										</button>
										<OptionalComponent display={!languageIsBase}>
											<button className="clear sharp btn-left-align delete" onClick={() => setLanguageToRemove(lang.code as LanguagesAbbr)}>
												<span>Remove from project</span>
											</button>
										</OptionalComponent>
									</NavigationDropdown>
								</OptionalComponent>
								<div onClick={() => handleLanguageDropdownOpen(lang.code)}>
									<Icon name={isOpen ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN} size={16} color="white" />
								</div>
							</div>
						</div>
						<OptionalComponent display={isOpen}>
							<div className="divider" />
							<div className="section">
								<div className="section-header">
									<div className="left">
										<Icon name={ICONS.TRANSLATIONS} size={20} color="white" />
										Page Translation
										<Tooltip tooltip="Auto-translated from your base language" maxWidth={167}>
											<Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-400)" />
										</Tooltip>
									</div>
									<Tooltip 
										tooltip="You cannot disable the base project language"
										disable={lang.code !== baseLanguage}
										maxWidth={167}
										position="left"
									>
										<Switch
											value="language_enablement"
											on={lang.code === baseLanguage ? true : !!workingSession?.languages.includes(lang.code)}
											disabled={lang.code === baseLanguage}
											onClick={() => handleLanguageEnablementToggle(lang.code as LanguagesAbbr)}
										/>
									</Tooltip>
								</div>
								<div className="content-area clickable" onClick={navigateToUpload}>
									<div className="left">
										Manage translations
										<Tooltip tooltip="Add custom page translations" maxWidth={167}>
											<Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-400)" />
										</Tooltip>
									</div>
									<div className="edit-button-background">
										<Icon name={ICONS.EDIT} size={10} color="white" />
									</div>
								</div>
							</div>
							<div className="divider" />
							<div className="section">
								<div className="section-header">
									<div className="left">
										<Icon name={ICONS.CAPTIONS} size={20} color="white" />
										Captions
									</div>
									<Switch
										value="TODO"
										on={false}
										onClick={() => alert('Not yet implement TODO')}
									/>
								</div>
								<div 
									className={classNames("content-area clickable", { 'cannot-edit': !canEditExtendedDictionary })}
									onClick={navigateToExtendedDictionary}
								>
									<div className="left">
										Extended Dictionary
										<Tooltip tooltip="These won't be included or translated in Captions" maxWidth={175}>
											<Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-400)" />
										</Tooltip>
									</div>
									<div className="edit-button-background">
										<Icon name={ICONS.EDIT} size={10} color="white" />
									</div>
									<OptionalComponent display={!canEditExtendedDictionary}>
										<div className="dictionary-not-editable-text">
											Not editable during live streaming
										</div>
									</OptionalComponent>
								</div>
							</div>
							<div className="divider" />
							<div className="section">
								<div className={classNames('section-header', { 'inactive': true })} >
									<div className="left">
										<Icon name={ICONS.THREE_STARS} size={20} color="white" />
										Transcripts
									</div>
								</div>

								{/* TODO The display logic is not yet implement */}
								<OptionalComponent display={Math.random() < 0}>
									<div className="transcripts-off-explainer">
										Auto-generated after a live session is over
									</div>
								</OptionalComponent>
								<OptionalComponent display={Math.random() > 0}>
									<div className="content-area">
										<div className="left">
											<Icon name={ICONS.DOCUMENTS_REVERSE} size={24} color="var(--gray-300)" />
											English_Captions.VTT
										</div>
										<div className="edit-button-background" onClick={() => alert('Not yet implement TODO')}>
											<Icon name={ICONS.THREE_DOTS_VERTICAL} size={12} color="" />
										</div>
									</div>
								</OptionalComponent>
							</div>
						</OptionalComponent>
					</div>
				);
			})}

			<div 
				className={classNames("add-language-button", { open: addLanguageDropdownOpen })}
				onClick={() => setAddLanguageDropdownOpen(!addLanguageDropdownOpen)}
				ref={addLanguageButtonRef}
			>
				<GradientText>
					+ Language
				</GradientText>
			</div>

			<div className={classNames("language-selection-dropdown", { open: addLanguageDropdownOpen })}>
				{Object.entries(languageLabelMapV2)
					.map(([languageCode, languageLabel]) => {
						// Don't show languages already added to the session
						if (languageOptionsSet.has(languageCode as string)) return;

						return (
							<div className="language-selection-dropdown-option" key={languageCode} onClick={() => setLanguageToAdd(languageCode as LanguagesAbbr)}>
								<img className="circle-flag" src={`https://assets.brandlive.com/assets/flags/round/${languageCode}.svg`} />
								{languageLabel} 
								<span className="editor-language-selector-language-code">{languageCode}</span>
							</div>
						);
					})
				}
			</div>
			<AddLanguageConfirmationModal
				languageToAdd={languageToAdd}
				handleClose={() => setLanguageToAdd(null)}
			/>
			<RemoveLanguageConfirmationModal
				languageToRemove={languageToRemove}
				handleClose={() => setLanguageToRemove(null)}
			/>
		</PanelBody>
	);
};

export default LanguageAndCaptions;
